// components/HeaderClient.tsx
"use client";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { isAuthenticated, logoutUser, getUser } from "@/services/auth";
import { useTenant, type BusinessType } from "@/context/TenantContext";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { bizKey } from "@/lib/businessLabels";
import type { LanguageType } from "@/services/language";
import styles from "../app/layout.module.scss";
import FeatureLockedModal from "@/components/FeatureLockedModal";
import { isTrustedLogoUrl } from "@/utils/helpers";

interface HeaderClientProps {
  serverIsLoggedIn?: boolean;
}

export default function HeaderClient({ serverIsLoggedIn = false }: HeaderClientProps) {
  const [open, setOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(serverIsLoggedIn);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showLoginWarning, setShowLoginWarning] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [lockedFeatureName, setLockedFeatureName] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const { tenantData, loading, clearTenantData, isSuperAdmin } = useTenant();

  const isFeatureEnabled = (featureKey: string): boolean => {
    if (isSuperAdmin) return true;
    if (!tenantData?.features) return false;
    return !!tenantData.features[featureKey];
  };
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = isAuthenticated();
      setIsLoggedIn(authenticated);
      if (authenticated) {
        const userData = getUser();
        setUser(userData);
      }
    };
    checkAuth();
    window.addEventListener("storage", checkAuth);

    return () =>
      window.removeEventListener("storage", checkAuth);

  }, [pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProfileDropdown]);
  const handleLinkClick = useCallback((e: React.MouseEvent, href: string, featureKey?: string) => {
    if (!isLoggedIn && href !== "/") {
      e.preventDefault();
      setShowLoginWarning(true);
      setOpen(false);
    } else if (featureKey && !isFeatureEnabled(featureKey)) {

      e.preventDefault();
      setLockedFeatureName(featureKey);
      setOpen(false);

    } else {

      setOpen(false);

    }

  }, [isLoggedIn, tenantData?.features]);

  const handleLogout = () => {
    logoutUser();
    clearTenantData();
    document.cookie = "isLoggedIn=; path=/; max-age=0";
    document.cookie = "auth_token=; path=/; max-age=0";
    setIsLoggedIn(false);
    setShowLogoutConfirm(false);
    setShowProfileDropdown(false);
    setOpen(false);
    navigate("/");
  };

  // Get display values with fallbacks
  const displayName = tenantData?.tenant?.name || "Saiman Parking";
  const rawLogoUrl = tenantData?.settings?.logoUrl;
  const displayLogo =
    isTrustedLogoUrl(rawLogoUrl)
      ? rawLogoUrl
      : "/saiman-parking.jpeg";
  const displayInitials = useMemo(() => {

    return displayName
      .split(" ")
      .map(word => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  }, [displayName]);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <>
      <header className={styles.header}>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
        `}</style>
        <div className={styles.headerContent}>
          {/* Left: Brand */}
          {loading && isLoggedIn ? (
            <div className={styles.brand} style={{ pointerEvents: 'none' }}>
              <div className={styles.logoIcon}>
                <div
                  style={{
                    width: '70px',
                    height: '40px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '8px',
                    animation: 'pulse 2s infinite',
                  }}
                />
              </div>
              <div className={styles.brandInfo}>
                <div
                  style={{
                    width: '150px',
                    height: '20px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    animation: 'pulse 2s infinite',
                  }}
                />
                <div
                  style={{
                    width: '200px',
                    height: '14px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '4px',
                    animation: 'pulse 2s infinite',
                  }}
                />
              </div>
            </div>
          ) : (
            <Link
              to="/"
              className={styles.brand}
              onClick={() => setOpen(false)}
            >
              <div className={styles.logoIcon}>
                <img
                  src={displayLogo}
                  alt={`${displayName} Logo`}
                  width={70}
                  height={40}
                  className={styles.logoImage}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/saiman-parking.avif";
                  }}
                />
              </div>
              <div className={styles.brandInfo}>
                <h1 className={styles.brandName}>{displayName}</h1>
              </div>

            </Link>
          )}

          {/* Center: Navigation — only shown when logged in */}
          {isLoggedIn && (() => {
            // SYSTEM_OWNER has no businessType; default to PARKING vocab so the
            // sidebar still renders something sensible on platform-admin views.
            const bt: BusinessType = tenantData?.tenant?.businessType ?? "PARKING";
            const recordsHref = bt === "RAILWAY_CLOAK_ROOM" ? "/item-list"
              : bt === "ROAD_TAX" ? "/tax-collection-list"
              : "/parking-list";

            return (
              <nav className={styles.nav}>
                <ul className={`${styles.navList} ${open ? styles.open : ""}`}>
                  <li>
                    <Link
                      to="/dashboard"
                      prefetch={true}
                      onClick={(e) => handleLinkClick(e, "/dashboard", "DASHBOARD")}
                      className={`${pathname === "/dashboard" ? styles.activeNav : ""} ${isFeatureEnabled("DASHBOARD") ? "" : styles.lockedFeatureLink}`}
                    >
                      {t('header.dashboard')}
                      {!isFeatureEnabled("DASHBOARD") && <span style={{ marginLeft: '4px' }}>🔒</span>}
                    </Link>
                  </li>

                  <li>
                    <Link
                      to={recordsHref}
                      prefetch={true}
                      onClick={(e) => handleLinkClick(e, recordsHref)}
                      className={pathname === recordsHref ? styles.activeNav : ""}
                    >
                      {t(bizKey(bt, 'navRecords'))}
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/finance"
                      prefetch={true}
                      onClick={(e) => handleLinkClick(e, "/finance", "FINANCE")}
                      className={`${pathname === "/finance" ? styles.activeNav : ""} ${isFeatureEnabled("FINANCE") ? "" : styles.lockedFeatureLink}`}
                    >
                      {t('header.finance')}
                      {!isFeatureEnabled("FINANCE") && <span style={{ marginLeft: '4px' }}>🔒</span>}
                    </Link>
                  </li>

                  {user?.role !== 'OPERATOR' && (
                    <li>
                      <Link
                        to="/audit-log"
                        prefetch={true}
                        onClick={(e) => handleLinkClick(e, "/audit-log", "AUDIT_LOGS")}
                        className={`${pathname === "/audit-log" ? styles.activeNav : ""} ${isFeatureEnabled("AUDIT_LOGS") ? "" : styles.lockedFeatureLink}`}
                      >
                        {t('header.auditLog')}
                        {!isFeatureEnabled("AUDIT_LOGS") && <span style={{ marginLeft: '4px' }}>🔒</span>}
                      </Link>
                    </li>
                  )}
                </ul>
              </nav>
            );
          })()}

          {/* Right: Profile Dropdown + Hamburger */}
          <div className={styles.rightSection}>
            {isLoggedIn && (
              <div className={styles.profileContainer} ref={dropdownRef}>
                <button
                  className={styles.profileButton}
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  aria-label="Profile menu"
                >
                  <div className={styles.ownerAvatar}>
                    <span>{displayInitials}</span>
                  </div>
                </button>

                {/* Dropdown Menu */}
                {showProfileDropdown && (
                  <div className={styles.profileDropdown}>
                    <div className={styles.dropdownHeader}>
                      <div className={styles.dropdownAvatar}>
                        <span>{displayInitials}</span>
                      </div>
                      <div className={styles.dropdownInfo}>
                        <span className={styles.dropdownName}>{displayName}</span>
                        <span className={styles.dropdownRole}>{t('header.administrator')}</span>
                      </div>
                    </div>

                    <div className={styles.dropdownDivider}></div>

                    <button
                      className={styles.dropdownItem}
                      onClick={() => {
                        toggleTheme();
                        setShowProfileDropdown(false);
                      }}
                    >
                      <span className={styles.dropdownIcon}>
                        {theme === 'dark' ? '☀️' : '🌙'}
                      </span>
                      {theme === 'dark' ? t('header.lightTheme') : t('header.darkTheme')}
                    </button>

                    {/* Organization Settings */}
                    <Link
                      to="/settings"
                      prefetch={true}
                      className={styles.dropdownItem}
                      onClick={() => setShowProfileDropdown(false)}
                      style={{ textDecoration: "none" }}
                    >
                      <span className={styles.dropdownIcon}>⚙️</span>
                      {t('header.settings')}
                    </Link>

                    {/* Language Selector */}
                    <div style={{ position: 'relative' }}>
                      <button
                        className={styles.dropdownItem}
                        onClick={() => setShowLangMenu(!showLangMenu)}
                      >
                        <span className={styles.dropdownIcon}>🌐</span>
                        {t('header.language')}
                        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', opacity: 0.7 }}>
                          {language === 'english' ? 'EN' : language === 'hindi' ? 'HI' : 'MR'}
                        </span>
                      </button>
                      {showLangMenu && (
                        <div style={{ paddingLeft: '2rem' }}>
                          {([['english', t('header.english')], ['hindi', t('header.hindi')], ['marathi', t('header.marathi')]] as [LanguageType, string][]).map(([key, label]) => (
                            <button
                              key={key}
                              className={styles.dropdownItem}
                              onClick={() => {
                                setLanguage(key);
                                setShowLangMenu(false);
                                setShowProfileDropdown(false);
                              }}
                              style={{ fontWeight: language === key ? 600 : 400 }}
                            >
                              {language === key && <span style={{ marginRight: '0.25rem' }}>✓</span>}
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <button
                      className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                      onClick={() => {
                        setShowProfileDropdown(false);
                        setShowLogoutConfirm(true);
                      }}
                    >
                      <span className={styles.dropdownIcon}>🚪</span>
                      {t('header.logout')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Hamburger for Mobile — only shown when logged in */}
            {isLoggedIn && (
              <button
                className={styles.hamburger}
                onClick={() => setOpen(!open)}
                aria-label="Toggle menu"
              >
                <span className={open ? styles.lineOpen : styles.line}></span>
                <span className={open ? styles.lineOpen : styles.line}></span>
                <span className={open ? styles.lineOpen : styles.line}></span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile nav overlay — closes menu when tapping outside */}
      {open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 98,
            background: 'transparent',
          }}
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
      {lockedFeatureName && (
        <FeatureLockedModal featureName={lockedFeatureName} onClose={() => setLockedFeatureName(null)} />
      )}

      {/* Login Warning Modal */}
      {showLoginWarning && (
        <div
          className={styles.logoutModal}
          onClick={() => setShowLoginWarning(false)}
        >
          <div
            className={styles.logoutCard}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.logoutIcon}>🔒</div>
            <h3>{t('header.loginRequired')}</h3>
            <p>{t('header.loginWarningMsg')}</p>

            <div className={styles.logoutActions}>
              <button
                className={styles.cancelBtn}
                onClick={() => setShowLoginWarning(false)}
              >
                {t('header.ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className={styles.logoutModal} onClick={() => setShowLogoutConfirm(false)}>
          <div className={styles.logoutCard} onClick={(e) => e.stopPropagation()}>
            <div className={styles.logoutIcon}>🚪</div>
            <h3>{t('header.logoutConfirmTitle')}</h3>
            <p>{t('header.logoutConfirm')}</p>
            <div className={styles.logoutActions}>
              <button
                className={styles.cancelBtn}
                onClick={() => setShowLogoutConfirm(false)}
              >
                {t('header.cancel')}
              </button>
              <button
                className={styles.confirmBtn}
                onClick={handleLogout}
              >
                {t('header.confirmLogout')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
